import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TabNavigation from '../../components/ui/TabNavigation';
import PrivacyToggle from '../../components/ui/PrivacyToggle';
import QuickActionFloat from '../../components/ui/QuickActionFloat';
import CalendarView from './components/CalendarView';
import SchedulingPanel from './components/SchedulingPanel';
import UpcomingSessionCard from './components/UpcomingSessionCard';
import PartnerAvailabilityToggle from './components/PartnerAvailabilityToggle';
import OptimalTimingSuggestions from './components/OptimalTimingSuggestions';

const IntimacyScheduler = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showSchedulingPanel, setShowSchedulingPanel] = useState(false);
  const [showPartnerAvailability, setShowPartnerAvailability] = useState(true);
  const [activeView, setActiveView] = useState('calendar');

  const scheduledSessions = [];

  const partnerAvailability = {};

  const optimalSuggestions = [];

  const [isLoadingProactive, setIsLoadingProactive] = useState(false);
  const [proactiveSuggestions, setProactiveSuggestions] = useState([]);
  
  const proactiveAnalysisService = {
    lastAnalysisTime: new Date()?.toISOString()
  };

  const loadProactiveSuggestions = () => {
    setIsLoadingProactive(true);
    // Simulate loading proactive suggestions
    setTimeout(() => {
      setProactiveSuggestions([]);
      setIsLoadingProactive(false);
    }, 1000);
  };

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setShowSchedulingPanel(true);
  };

  const handleSchedule = (sessionData) => {
    console.log('Scheduling session:', sessionData);
    setShowSchedulingPanel(false);
    setSelectedDate(null);
  };

  const handleCancelScheduling = () => {
    setShowSchedulingPanel(false);
    setSelectedDate(null);
  };

  const handleModifySession = (session) => {
    setSelectedDate(new Date(session.date));
    setShowSchedulingPanel(true);
  };

  const handleCancelSession = (sessionId) => {
    console.log('Canceling session:', sessionId);
  };

  const handleSelectSuggestion = (suggestion) => {
    setSelectedDate(new Date(suggestion.date));
    setShowSchedulingPanel(true);
  };

  const monthYear = currentDate?.toLocaleDateString('en-US', { 
    month: 'long', 
    year: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-background">
      <TabNavigation />
      <PrivacyToggle />
      <QuickActionFloat />
      <div className="pt-[60px] pb-20 lg:pb-6 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 md:mb-8">
            <h1 className="text-3xl md:text-4xl font-heading font-semibold text-foreground mb-2">
              Intimacy Scheduler
            </h1>
            <p className="text-base md:text-lg text-muted-foreground">
              Coordinate intimate moments with AI-powered compatibility insights
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <button
                onClick={handlePreviousMonth}
                className="p-2 rounded-md border border-border bg-card hover:bg-muted transition-therapeutic"
                aria-label="Previous month"
              >
                <Icon name="ChevronLeft" size={20} color="var(--color-foreground)" />
              </button>
              
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground min-w-[180px] md:min-w-[200px] text-center">
                {monthYear}
              </h2>
              
              <button
                onClick={handleNextMonth}
                className="p-2 rounded-md border border-border bg-card hover:bg-muted transition-therapeutic"
                aria-label="Next month"
              >
                <Icon name="ChevronRight" size={20} color="var(--color-foreground)" />
              </button>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-3 w-full sm:w-auto">
              <PartnerAvailabilityToggle
                isVisible={showPartnerAvailability}
                onToggle={() => setShowPartnerAvailability(!showPartnerAvailability)}
                partnerName="Sarah"
              />
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-6 mb-6">
            <div className="flex-1">
              <CalendarView
                currentDate={currentDate}
                selectedDate={selectedDate}
                onDateSelect={handleDateSelect}
                scheduledSessions={scheduledSessions}
                partnerAvailability={showPartnerAvailability ? partnerAvailability : {}}
              />
            </div>

            {showSchedulingPanel && selectedDate && (
              <div className="lg:w-[400px]">
                <SchedulingPanel
                  selectedDate={selectedDate}
                  onSchedule={handleSchedule}
                  onCancel={handleCancelScheduling}
                />
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2 border-b border-border mb-6">
            <button
              onClick={() => setActiveView('calendar')}
              className={`
                px-4 py-3 text-sm md:text-base font-medium font-caption
                border-b-2 transition-therapeutic
                ${activeView === 'calendar' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }
              `}
            >
              Upcoming Sessions
            </button>
            <button
              onClick={() => setActiveView('suggestions')}
              className={`
                px-4 py-3 text-sm md:text-base font-medium font-caption
                border-b-2 transition-therapeutic
                ${activeView === 'suggestions' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                }
              `}
            >
              AI Suggestions
            </button>
          </div>

          {activeView === 'calendar' && (
            <div>
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                  Scheduled Sessions
                </h3>
                <span className="text-sm text-muted-foreground">
                  {scheduledSessions?.length} session{scheduledSessions?.length !== 1 ? 's' : ''}
                </span>
              </div>

              {scheduledSessions?.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                  {scheduledSessions?.map(session => (
                    <UpcomingSessionCard
                      key={session?.id}
                      session={session}
                      onModify={handleModifySession}
                      onCancel={handleCancelSession}
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-card rounded-lg border border-border p-8 md:p-12 text-center">
                  <div className="flex justify-center mb-4">
                    <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full">
                      <Icon name="Calendar" size={32} color="var(--color-muted-foreground)" />
                    </div>
                  </div>
                  <h4 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-2">
                    No Scheduled Sessions
                  </h4>
                  <p className="text-sm md:text-base text-muted-foreground mb-6">
                    Select a date on the calendar to schedule your first intimate session
                  </p>
                  <Button
                    variant="default"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={() => handleDateSelect(new Date())}
                  >
                    Schedule Now
                  </Button>
                </div>
              )}
            </div>
          )}

          {activeView === 'suggestions' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <OptimalTimingSuggestions
                suggestions={optimalSuggestions}
                onSelectTime={handleSelectSuggestion}
              />
            </div>
          )}

          {activeView === 'proactive' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-accent/10 to-primary/10 rounded-lg border border-accent/20 p-6">
                <div className="flex items-start space-x-3 mb-4">
                  <Icon name="Sparkles" size={24} color="var(--color-accent)" />
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                      Proactive AI Insights
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Claude analyzes your mood patterns, availability history, and preferences to suggest optimal encounter windows before you even open the app.
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="Clock" size={16} color="var(--color-muted-foreground)" />
                  <span>
                    Last analysis: {proactiveAnalysisService?.lastAnalysisTime 
                      ? new Date(proactiveAnalysisService?.lastAnalysisTime)?.toLocaleString()
                      : 'Running now...'}
                  </span>
                </div>
              </div>

              {isLoadingProactive ? (
                <div className="bg-card rounded-lg border border-border p-12 text-center">
                  <Icon name="Loader" size={48} color="var(--color-primary)" className="animate-spin mx-auto mb-4" />
                  <p className="text-lg font-medium text-foreground mb-2">Analyzing Your Patterns...</p>
                  <p className="text-sm text-muted-foreground">
                    Claude is reviewing your mood history, availability patterns, and preferences to find optimal encounter windows.
                  </p>
                </div>
              ) : proactiveSuggestions?.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {proactiveSuggestions?.map((suggestion, index) => (
                    <div
                      key={suggestion?.id}
                      className="bg-card rounded-lg border border-border p-6 hover:shadow-therapeutic transition-therapeutic"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="text-lg font-heading font-semibold text-foreground">
                              {suggestion?.day}
                            </span>
                            {index === 0 && (
                              <span className="px-2 py-0.5 bg-accent text-white text-xs font-medium rounded">
                                Top Match
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {suggestion?.time} • {suggestion?.duration} minutes
                          </p>
                        </div>
                        <div className="flex items-center space-x-1 px-3 py-1.5 rounded-md bg-success/10">
                          <Icon name="TrendingUp" size={14} color="var(--color-success)" />
                          <span className="text-sm font-medium text-success">
                            {suggestion?.compatibilityScore}%
                          </span>
                        </div>
                      </div>

                      <div className="space-y-3 mb-4">
                        <div className="flex items-start space-x-2">
                          <Icon name="Brain" size={16} color="var(--color-accent)" className="mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">AI Reasoning</p>
                            <p className="text-sm text-foreground">{suggestion?.reasoning}</p>
                          </div>
                        </div>

                        <div className="flex items-start space-x-2">
                          <Icon name="Heart" size={16} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Mood Prediction</p>
                            <p className="text-sm text-foreground">{suggestion?.moodPrediction}</p>
                          </div>
                        </div>

                        <div className="flex items-start space-x-2">
                          <Icon name="Activity" size={16} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Energy Prediction</p>
                            <p className="text-sm text-foreground">{suggestion?.energyPrediction}</p>
                          </div>
                        </div>
                      </div>

                      <Button
                        variant="primary"
                        size="sm"
                        iconName="Calendar"
                        iconPosition="left"
                        onClick={() => handleSelectSuggestion(suggestion)}
                        fullWidth
                      >
                        Schedule This Window
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-card rounded-lg border border-border p-12 text-center">
                  <Icon name="Calendar" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                  <p className="text-lg font-medium text-foreground mb-2">Building Your Pattern Profile</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Keep logging your moods and scheduling encounters. Claude will analyze your patterns to provide proactive suggestions.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="RefreshCw"
                    iconPosition="left"
                    onClick={loadProactiveSuggestions}
                  >
                    Refresh Analysis
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IntimacyScheduler;